// src/components/DeliveryTimeline.js — Maxula
// Lot E — Frise visuelle de suivi de livraison (acheteur). S'adapte au type
// de produit (physique / digital). N'utilise que des données déjà présentes
// sur la commande (deliveryStatus, deliveredAt, receivedAt, carrier,
// trackingNumber, autoConfirmAt). Icônes react-icons (aucun emoji).
import React, { useState } from 'react';
import { FiCheck, FiTruck, FiCopy, FiCheckCircle, FiExternalLink, FiClock } from 'react-icons/fi';
import { carrierTracking } from '../utils/carriers';

const PHYSICAL_STEPS = [
  { key: 'pending', label: 'En préparation' },
  { key: 'accepted', label: 'Acceptée' },
  { key: 'prepared', label: 'Préparée' },
  { key: 'shipped', label: 'Expédiée' },
  { key: 'delivered', label: 'Livrée' },
  { key: 'received', label: 'Réceptionnée' },
];
const DIGITAL_STEPS = [
  { key: 'pending', label: 'En préparation' },
  { key: 'delivered', label: 'Accès remis' },
  { key: 'received', label: 'Réception confirmée' },
];

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (e) { return ''; }
};
const daysLeft = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);

export default function DeliveryTimeline({ order, productType }) {
  const [copied, setCopied] = useState(false);
  if (!order) return null;

  const type = productType || order.productType || 'physical';
  const steps = type === 'digital' ? DIGITAL_STEPS : PHYSICAL_STEPS;
  const status = order.deliveryStatus || 'pending';
  let curIdx = steps.findIndex((s) => s.key === status);
  if (curIdx < 0) curIdx = 0;

  const stampFor = (key) => {
    if (key === 'delivered' && order.deliveredAt) return fmtDate(order.deliveredAt);
    if (key === 'received' && order.receivedAt) return fmtDate(order.receivedAt);
    return '';
  };

  const track = carrierTracking(order.carrier, order.trackingNumber);
  const copyTn = async () => {
    try { await navigator.clipboard.writeText(String(order.trackingNumber || '')); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (e) { /* clipboard indisponible */ }
  };

  return (
    <div>
      <ol className="space-y-0">
        {steps.map((s, i) => {
          const done = i < curIdx;
          const active = i === curIdx;
          const last = i === steps.length - 1;
          const dotCls = done
            ? 'bg-green-600 text-white border-green-600'
            : active
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-300 border-gray-300';
          const lineCls = i < curIdx ? 'bg-green-600' : 'bg-gray-200';
          const stamp = stampFor(s.key);
          return (
            <li key={s.key} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${dotCls}`}>
                  {done ? <FiCheck size={13} /> : (active ? <FiCheck size={13} /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />)}
                </span>
                {!last && <span className={`w-0.5 flex-1 min-h-[20px] ${lineCls}`} />}
              </div>
              <div className={`pb-4 ${last ? '' : ''}`}>
                <p className={`text-sm font-medium ${done || active ? 'text-gray-900' : 'text-gray-400'}`}>{s.label}</p>
                {stamp && <p className="text-xs text-gray-500">{stamp}</p>}
              </div>
            </li>
          );
        })}
      </ol>

      {(order.carrier || order.trackingNumber) && (
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50/60 p-3 text-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <FiTruck size={15} className="text-gray-500" />
            <span className="font-medium">{order.carrier || 'Transporteur'}</span>
            {order.trackingNumber && <span className="text-gray-500">· {order.trackingNumber}</span>}
          </div>
          {order.trackingNumber && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {track && (
                <a href={track.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700">
                  <FiExternalLink size={13} /> {track.prefilled ? 'Suivre le colis' : 'Page de suivi'}
                </a>
              )}
              <button type="button" onClick={copyTn}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-100">
                <FiCopy size={13} /> {copied ? 'Copié' : 'Copier le suivi'}
              </button>
            </div>
          )}
        </div>
      )}

      {order.deliveryStatus === 'delivered' && order.autoConfirmAt && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
          <FiClock size={13} className="mt-0.5 shrink-0" />
          Sans action de votre part, la réception sera confirmée automatiquement le {fmtDate(order.autoConfirmAt)}
          {daysLeft(order.autoConfirmAt) > 0 ? ` (dans ${daysLeft(order.autoConfirmAt)} j)` : ''}.
        </p>
      )}
      {order.deliveryStatus === 'received' && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-green-700">
          <FiCheckCircle size={13} /> Réception confirmée — commande clôturée.
        </p>
      )}
    </div>
  );
}
